<div class="container">
  <section class="settings-section">
    <div class="d-flex align-items-center justify-content-between">
      <h2 class="section-title">General</h2>
    </div>
    <hr />
    <div class="card setting-card">
      <div class="card-body d-flex align-items-center justify-content-between">
        <div>
          <div class="setting-title">Auto-collapse side menu on inactivity</div>
          <div class="setting-desc">
            When enabled, the side navigation hides when the pointer leaves the edge.
          </div>
        </div>
        <div class="form-check form-switch">
          <input
            class="form-check-input"
            type="checkbox"
            id="autoCollapseSwitch"
            [checked]="(settings.autoCollapse$ | async) ?? false"
            (change)="toggleAutoCollapse($event)"
            />
        </div>
      </div>
    </div>
  </section>

  <section class="settings-section">
    <div class="d-flex align-items-center justify-content-between">
      <h2 class="section-title">Appearance</h2>
    </div>
    <hr />
    <div class="card setting-card">
      <div class="card-body">
        <div class="setting-title mb-2">Color theme</div>
        <div class="setting-desc mb-3">Choose a theme for the application interface.</div>

        <div class="theme-options">
          <label class="theme-tile">
            <input type="radio" name="theme" value="light" (change)="onThemeChange('light')" [checked]="(theme.currentTheme$ | async) === 'light'" />
            <div class="tile-visual light">
              <div class="tile-header"></div>
              <div class="tile-body"></div>
            </div>
            <div class="tile-label">Light</div>
          </label>

          <label class="theme-tile">
            <input type="radio" name="theme" value="dark" (change)="onThemeChange('dark')" [checked]="(theme.currentTheme$ | async) === 'dark'" />
            <div class="tile-visual dark">
              <div class="tile-header"></div>
              <div class="tile-body"></div>
            </div>
            <div class="tile-label">Dark</div>
          </label>

          <label class="theme-tile">
            <input type="radio" name="theme" value="system" (change)="onThemeChange('system')" [checked]="(theme.currentTheme$ | async) === 'system'" />
            <div class="tile-visual system">
              <div class="tile-header"></div>
              <div class="tile-body"></div>
            </div>
            <div class="tile-label">System</div>
          </label>

          <label class="theme-tile">
            <input type="radio" name="theme" value="custom" (change)="onThemeChange('custom')" [checked]="(theme.currentTheme$ | async) === 'custom'" />
            <div class="tile-visual custom">
              <div class="tile-header"></div>
              <div class="tile-body"></div>
            </div>
            <div class="tile-label">Custom</div>
          </label>
        </div>

        @if ((theme.currentTheme$ | async) === 'custom') {
          <div class="custom-theme-editor mt-3">
            <div class="row g-3">
              <div class="col-md-3">
                <label class="form-label">Primary</label>
                <input class="form-control form-control-color" type="color" [(ngModel)]="custom.primary" />
              </div>
              <div class="col-md-3">
                <label class="form-label">Primary Contrast</label>
                <input class="form-control form-control-color" type="color" [(ngModel)]="custom.primaryContrast" />
              </div>
              <div class="col-md-3">
                <label class="form-label">Background</label>
                <input class="form-control form-control-color" type="color" [(ngModel)]="custom.bg" />
              </div>
              <div class="col-md-3">
                <label class="form-label">Surface</label>
                <input class="form-control form-control-color" type="color" [(ngModel)]="custom.surface" />
              </div>
              <div class="col-md-3">
                <label class="form-label">Border</label>
                <input class="form-control form-control-color" type="color" [(ngModel)]="custom.border" />
              </div>
              <div class="col-md-3">
                <label class="form-label">Text</label>
                <input class="form-control form-control-color" type="color" [(ngModel)]="custom.text" />
              </div>
              <div class="col-md-3">
                <label class="form-label">Muted Text</label>
                <input class="form-control form-control-color" type="color" [(ngModel)]="custom.muted" />
              </div>
            </div>
            <div class="mt-3 d-flex gap-2">
              <button class="btn btn-primary" (click)="applyCustom()">Apply custom theme</button>
              <button class="btn btn-light" (click)="resetCustom()">Reset</button>
            </div>
          </div>
        }
      </div>
    </div>
  </section>

  <section class="settings-section">
    <div class="d-flex align-items-center justify-content-between">
      <h2 class="section-title">Integrations</h2>
      @if (integrationsLoading) {
        <span class="small text-muted">Checking connection status...</span>
      }
    </div>
    <hr />

    <div class="card setting-card mb-3">
      <div class="card-body">
        <div class="setting-title mb-2">Provider API Keys (per user)</div>
        <div class="setting-desc mb-3">Your keys are stored server-side and shown as masked values only.</div>

        @for (provider of providers; track provider) {
          <div class="provider-row mb-3">
            <div class="provider-row__meta">
              <div class="fw-semibold">{{ providerLabel(provider) }}</div>
              <div class="small provider-status-line">
                @if (providerStatus(provider)?.connected) {
                  <span class="provider-status provider-status--connected">Connected</span>
                  <code>{{ providerStatus(provider)?.maskedApiKey }}</code>
                } @else {
                  <span class="provider-status provider-status--disconnected">Not connected</span>
                }
              </div>
            </div>
            <div class="provider-row__actions">
              <input
                class="form-control form-control-sm"
                [placeholder]="'Paste ' + providerLabel(provider) + ' key'"
                [(ngModel)]="providerInputs[provider]"
              />
              <button
                class="btn btn-primary btn-sm"
                (click)="saveProvider(provider)"
                [disabled]="integrationBusy[provider]"
              >
                Save
              </button>
              <button
                class="btn btn-outline-danger btn-sm"
                (click)="removeProvider(provider)"
                [disabled]="integrationBusy[provider] || !isProviderConnected(provider)"
              >
                Remove
              </button>
            </div>
          </div>
        }
      </div>
    </div>

    <div class="card setting-card mb-3">
      <div class="card-body">
        <div class="setting-title mb-2">Default model routing</div>
        <div class="setting-desc mb-3">
          Pick a global fallback model, then optionally assign a specific model to each agent-enabled surface.
        </div>
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label">Default provider</label>
            <select class="form-select" [(ngModel)]="integrationPreferences.defaultProvider">
              <option value="">System default</option>
              <option value="google-genai">Google Gemini</option>
              <option value="openai">OpenAI</option>
              <option value="anthropic">Anthropic</option>
              <option value="xai">xAI</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label">Default model</label>
            <select class="form-select" [(ngModel)]="integrationPreferences.defaultModel">
              <option value="">System default</option>
              @for (model of modelOptions; track model.id) {
                <option [value]="model.id">{{ model.label }}</option>
              }
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label">Fallback model if no surface model is set</label>
            <input class="form-control" [value]="modelLabel(integrationPreferences.defaultModel || fallbackModelId)" readonly />
          </div>
        </div>
        <hr class="my-4" />
        <div class="d-flex align-items-center justify-content-between mb-2">
          <div>
            <div class="fw-semibold">Agent surfaces</div>
            <div class="small text-muted">Each surface uses its assigned model first, then falls back to the global default if unset.</div>
          </div>
        </div>
        <div class="row g-3">
          @for (surface of modelSurfaces; track surface.key) {
            <div class="col-12 col-md-6 col-xl-4">
              <label class="form-label">{{ surface.label }}</label>
              <select
                class="form-select"
                [ngModel]="surfaceModelValue(surface.key)"
                (ngModelChange)="onSurfaceModelChange(surface.key, $event)"
              >
                <option value="">Use default model</option>
                @for (model of modelOptions; track model.id) {
                  <option [value]="model.id">{{ model.label }}</option>
                }
              </select>
            </div>
          }
        </div>
        <div class="mt-3 d-flex gap-2">
          <button class="btn btn-primary btn-sm" (click)="saveIntegrationPreferences()">Save model preferences</button>
        </div>
      </div>
    </div>

    <div class="card setting-card">
      <div class="card-body">
        <div class="setting-title mb-2">GitHub accounts</div>
        <div class="setting-desc mb-3">
          Repos can fall back to the organization account, but your personal GitHub connection is what enables personal repo creation and imports.
        </div>
        <div class="row g-3">
          <div class="col-12">
            <div class="fw-semibold mb-2">Organization account</div>
            <div class="small text-muted mb-2">
              This is the shared fallback account used when no personal GitHub connection is available.
            </div>
            <div class="github-account-panel">
              <div class="small text-muted">Account</div>
              <div class="fw-semibold github-account-panel__value">{{ githubBusinessInfo.accountLogin || 'the-machine' }}</div>
            </div>
          </div>
          <div class="col-12">
            <div class="fw-semibold mb-2">Personal GitHub</div>
            <div class="small text-muted mb-2">
              Connect one or more personal GitHub accounts. The active one will be used for new repo creation by default.
            </div>
            <div class="github-account-panel github-account-panel--stacked">
              <div>
                <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
                  <div>
                    <div class="small text-muted">Currently installed personal GitHubs</div>
                    <div class="small text-muted">Live GitHub App installations on user accounts.</div>
                  </div>
                  <button class="btn btn-outline-secondary btn-sm" type="button" (click)="loadLivePersonalGithubInstallations(true)" [disabled]="githubInstallationsLoading">
                    @if (githubInstallationsLoading) {
                      Refreshing...
                    } @else {
                      Refresh
                    }
                  </button>
                </div>
                @if (livePersonalGithubInstallations.length > 0) {
                  <div class="github-connection-list">
                    @for (installation of livePersonalGithubInstallations; track installation.installationId) {
                      <div class="github-connection-item" [class.github-connection-item--active]="installation.active">
                        <div>
                          <div class="small text-muted">Installed on GitHub</div>
                          <div class="fw-semibold github-account-panel__value">
                            {{ installation.accountLogin || ('Installation #' + installation.installationId) }}
                          </div>
                        </div>
                        <div class="github-connection-item__actions">
                          @if (installation.active) {
                            <span class="badge text-bg-primary">Saved and active</span>
                          } @else if (installation.saved) {
                            <span class="badge text-bg-secondary">Saved</span>
                          } @else {
                            <span class="badge text-bg-light github-installation-badge">Installed only</span>
                          }
                          <span class="small text-muted">#{{ installation.installationId }}</span>
                        </div>
                      </div>
                    }
                  </div>
                } @else if (!githubInstallationsLoading) {
                  <div class="small text-muted">No live personal GitHub app installations found yet.</div>
                }
              </div>
              <div class="github-account-panel__manual">
              @if (personalGithubConnections.length > 0) {
                <div class="github-connection-list">
                  @for (connection of personalGithubConnections; track connection.installationId) {
                    <div class="github-connection-item" [class.github-connection-item--active]="connection.active">
                      <div>
                        <div class="small text-muted">Personal account</div>
                        <div class="fw-semibold github-account-panel__value">
                          {{ connection.accountLogin || ('Installation #' + connection.installationId) }}
                        </div>
                      </div>
                      <div class="github-connection-item__actions">
                        @if (connection.active) {
                          <span class="badge text-bg-primary">Active</span>
                        } @else {
                          <button class="btn btn-outline-secondary btn-sm" type="button" (click)="setActivePersonalGithubConnection(connection.installationId)">
                            Use this account
                          </button>
                        }
                        <button class="btn btn-outline-danger btn-sm" type="button" (click)="removePersonalGithubConnection(connection.installationId)">
                          Remove
                        </button>
                      </div>
                    </div>
                  }
                </div>
              } @else {
                <div class="small text-muted">No personal GitHub accounts connected yet.</div>
              }
              </div>
              <div class="github-account-panel__actions">
                <button class="btn btn-primary btn-sm" (click)="connectPersonalGithub()" [disabled]="githubConnectLoading">
                  @if (!githubConnectLoading) {
                    @if (personalGithubConnections.length > 0) {
                      Connect another personal GitHub
                    } @else {
                      Connect personal GitHub
                    }
                  } @else {
                    Connecting...
                  }
                </button>
                @if (personalGithubConnections.length > 0) {
                  <button class="btn btn-outline-danger btn-sm" (click)="clearGithubContext()">Clear all</button>
                }
                <button class="btn btn-outline-secondary btn-sm" type="button" (click)="showManualGithubEntry = !showManualGithubEntry">
                  @if (showManualGithubEntry) {
                    Hide manual entry
                  } @else {
                    Manual entry
                  }
                </button>
              </div>
              @if (showManualGithubEntry) {
                <div class="github-account-panel__manual">
                  <div class="small text-muted mb-2">Add personal account manually</div>
                  <input class="form-control mb-2" [(ngModel)]="githubContext.personalInstallationId" placeholder="Personal installation ID" />
                  <input class="form-control mb-2" [(ngModel)]="githubContext.personalAccountLogin" placeholder="Personal account login" />
                  <button class="btn btn-outline-primary btn-sm" (click)="saveGithubContext()">Add personal GitHub</button>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

</div>
